import 'amazon-connect-streams';
import { useEffect, useRef } from 'react';
import { EmailClient } from '@amazon-connect/email';
import { useEmailContext } from '../../../contexts/EmailContext';
import { extractEmailData } from '../../../utils/emailContactHelpers';
import { fetchEmailBody } from '../../../services/fetchEmailBody';
import { EmailMessage } from '../../../models/Email';
import { fetchEmailThread, ThreadContact } from '../../../services/fetchEmailThread';
import { resolveEmailAttachmentsWithStatus } from '../../../services/resolveEmailAttachmentsWithStatus';
import { EMAIL_INITIATION_METHODS } from '../../../constants/email';
import DOMPurify from 'dompurify';
import { useEmailSanitizationLogger } from './useEmailSanitizationLogger';

export function filterThreadContacts(
  contacts: ThreadContact[],
  activeContactId: string
): { filtered: ThreadContact[]; wasTransferred: boolean } {
  const previousContactIdsSet = new Set(contacts.map(c => c.previousContactId).filter(Boolean));
  const filtered = contacts.filter(c => c.contactId === activeContactId || !previousContactIdsSet.has(c.contactId));
  const activeContact = contacts.find(c => c.contactId === activeContactId);
  const wasTransferred =
    !!activeContact && activeContact.initiationMethod.toUpperCase() === EMAIL_INITIATION_METHODS.TRANSFER;
  return { filtered, wasTransferred };
}

interface UseEmailContactHandlersProps {
  onError?: (message: string) => void;
}

export function useEmailContactHandlers({ onError }: UseEmailContactHandlersProps): void {
  const { updateEmailData, updateEmailThread, startLoadingThread, stopLoadingThread } = useEmailContext();
  const { logSanitization } = useEmailSanitizationLogger();
  const isInitializedRef = useRef(false);
  const emailClientRef = useRef<EmailClient | null>(null);
  const onErrorRef = useRef(onError);

  onErrorRef.current = onError;

  useEffect(() => {
    if (isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    const handleExtractEmailData = (contact: connect.Contact): void => {
      updateEmailData(contact.getContactId(), extractEmailData(contact));
    };

    const handleFetchEmailThread = async (contact: connect.Contact): Promise<void> => {
      const client = emailClientRef.current;

      if (!client) return;

      const activeContactId = contact.getContactId();
      const contactAssociationId = contact.getContactAssociationId();
      if (!contactAssociationId) return;

      try {
        startLoadingThread(activeContactId);
        const contacts = await fetchEmailThread(contactAssociationId);

        const { filtered: filteredContacts, wasTransferred } = filterThreadContacts(contacts, activeContactId);

        const messages: EmailMessage[] = await Promise.all(
          filteredContacts.map(async threadContact => {
            const emailData = await client.getEmailData({
              contactId: threadContact.contactId,
              activeContactId
            });

            const { bodyLocation, attachmentLocations, subject, from, to, cc, direction, deliveredTo } = emailData;

            const allToRecipients = [...(deliveredTo ? [deliveredTo] : []), ...(to ?? [])];

            let bodyHtml: string | undefined;

            if (bodyLocation) {
              const rawBodyHtml = await fetchEmailBody(bodyLocation, threadContact.contactId);
              if (rawBodyHtml) {
                logSanitization({
                  contactId: threadContact.contactId,
                  outcome: 'attempted'
                });

                try {
                  const sanitizedHtml = DOMPurify.sanitize(rawBodyHtml);
                  const htmlWasModified = sanitizedHtml !== rawBodyHtml;

                  logSanitization({
                    contactId: threadContact.contactId,
                    outcome: htmlWasModified ? 'modified' : 'unchanged'
                  });

                  bodyHtml = sanitizedHtml;
                } catch (error) {
                  logSanitization({
                    contactId: threadContact.contactId,
                    outcome: 'failed',
                    error
                  });
                }
              }
            }

            const { attachments, hasRejectedAttachments } = await resolveEmailAttachmentsWithStatus(
              attachmentLocations,
              threadContact.contactId
            );

            return {
              contactId: threadContact.contactId,
              subject,
              from,
              to: allToRecipients,
              cc,
              bodyHtml,
              attachments,
              timestamp: new Date(threadContact.initiationTimestamp).getTime(),
              direction,
              hasRejectedAttachments
            };
          })
        ).then(msgs => msgs.sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0)));

        updateEmailThread(activeContactId, { contactAssociationId, messages, wasTransferred });
      } catch (error) {
        console.error('Failed to fetch email thread:', error);
        onErrorRef.current?.('Failed to load the email thread. Please try again.');
      } finally {
        stopLoadingThread(activeContactId);
      }
    };

    const handleEmailContact = (contact: connect.Contact): void => {
      handleExtractEmailData(contact);

      contact.onConnected(() => {
        handleExtractEmailData(contact);
        void handleFetchEmailThread(contact);
      });
    };

    connect.agent(agent => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      emailClientRef.current = new EmailClient(connect.core.getSDKClientConfig() as any);

      agent
        .getContacts()
        .filter(c => (c.getType() as string) === connect.ContactType.EMAIL)
        .filter(c => c.getInitiationMethod()?.toUpperCase() !== EMAIL_INITIATION_METHODS.AGENT_REPLY)
        .forEach(handleEmailContact);
    });

    connect.contact(contact => {
      if ((contact.getType() as string) !== connect.ContactType.EMAIL) {
        return;
      }

      const initiationMethod = contact.getInitiationMethod() as string;

      if (initiationMethod?.toUpperCase() === EMAIL_INITIATION_METHODS.AGENT_REPLY) {
        return;
      }

      handleEmailContact(contact);
    });
  }, [logSanitization, startLoadingThread, stopLoadingThread, updateEmailData, updateEmailThread]);
}
